// テーブル内にカーソルがある時、 セル付近に floating action bar を出す。
// Notion / Linear 風: 編集中の cell の上に小さなフローティングメニュー。
//
// 動作:
//   1. editor.registerUpdateListener で selection を監視
//   2. selection が table cell 内なら、 そのセルの上に menu を表示
//   3. それ以外は hide
// 内容: 行追加/削除、 列追加/削除、 ヘッダー切替、 テーブル削除

import { $getSelection, $isRangeSelection } from "lexical"
import {
  $getTableCellNodeFromLexicalNode,
  $isTableCellNode,
  $isTableSelection,
} from "@lexical/table"
import {
  ArrowUpToLine, ArrowDownToLine,   // 行 (上 / 下に追加)
  ArrowLeftToLine, ArrowRightToLine,// 列 (左 / 右に追加)
  Minus,                            // 行/列削除 (1 つ削る)
  Trash2,                           // テーブル全体削除 (ゴミ箱)
  Heading, Columns3,                // 行/列ヘッダー切替
  AlignLeft, AlignCenter, AlignRight, // 整列
  Merge, Split,                     // セル結合/解除
  Palette,                          // 背景色
  createElement,
} from "lucide"

import {
  insertRowAbove, insertRowBelow, deleteRow,
  insertColumnLeft, insertColumnRight, deleteColumn,
  toggleHeaderRow, toggleHeaderColumn, deleteTable,
  mergeCells, unmergeCells,
  alignLeft, alignCenter, alignRight,
  setCellBackground, CELL_BG_COLORS,
} from "./table_actions"

function svg(IconNode, cls = "w-3.5 h-3.5") {
  const el = createElement(IconNode)
  el.setAttribute("class", cls)
  el.setAttribute("stroke-width", "2")
  return el.outerHTML
}

function makeBtn({ title, icon, onClick }) {
  const b = document.createElement("button")
  b.type = "button"
  b.tabIndex = -1
  b.className = "akebono-tb-btn"
  b.title = title
  b.setAttribute("aria-label", title)
  b.innerHTML = icon
  b.addEventListener("mousedown", (e) => e.preventDefault())
  b.addEventListener("click", (e) => { e.preventDefault(); onClick() })
  return b
}

function sep() {
  const s = document.createElement("span")
  s.className = "akebono-tb-sep"
  return s
}

export function attachTableFloatingMenu(editor, surface) {
  const menu = document.createElement("div")
  menu.className = "akebono-table-floating"
  menu.style.position = "fixed"
  menu.style.zIndex = "50"
  menu.style.display = "none"

  // 複数選択時に「2 × 3」 のような選択範囲表示
  const summary = document.createElement("span")
  summary.className = "akebono-table-floating-summary"
  summary.style.display = "none"

  // 結合/解除 ボタン (条件によって show/hide)
  const mergeBtn   = makeBtn({ title: "選択セルを結合", icon: svg(Merge),
                                onClick: () => mergeCells(editor) })
  const unmergeBtn = makeBtn({ title: "結合を解除",       icon: svg(Split),
                                onClick: () => unmergeCells(editor) })
  mergeBtn.style.display   = "none"
  unmergeBtn.style.display = "none"

  // 背景色ピッカー (color スウォッチを並べた popup)
  const colorBtn   = makeBtn({ title: "セル背景色", icon: svg(Palette),
                                onClick: (e) => toggleColorPicker(e) })
  const colorPopup = document.createElement("div")
  colorPopup.className = "akebono-table-color-popup"
  colorPopup.style.display = "none"
  CELL_BG_COLORS.forEach((c) => {
    const sw = document.createElement("button")
    sw.type = "button"
    sw.tabIndex = -1
    sw.className = "akebono-color-swatch"
    sw.title = c.label
    sw.style.background = c.css
    if (c.border) sw.style.border = `1px solid ${c.border}`
    sw.addEventListener("mousedown", (ev) => ev.preventDefault())
    sw.addEventListener("click", (ev) => {
      ev.preventDefault()
      setCellBackground(editor, c.value)
      colorPopup.style.display = "none"
    })
    colorPopup.appendChild(sw)
  })
  document.body.appendChild(colorPopup)

  function toggleColorPicker(e) {
    if (colorPopup.style.display === "grid") {
      colorPopup.style.display = "none"
      return
    }
    const btnRect = colorBtn.getBoundingClientRect()
    colorPopup.style.display = "grid"
    // 位置を colorBtn の真下に
    requestAnimationFrame(() => {
      colorPopup.style.left = `${btnRect.left}px`
      colorPopup.style.top  = `${btnRect.bottom + 4}px`
    })
  }
  document.addEventListener("mousedown", (e) => {
    if (!colorPopup.contains(e.target) && !colorBtn.contains(e.target)) {
      colorPopup.style.display = "none"
    }
  })

  menu.append(
    summary,
    // 行
    makeBtn({ title: "行を上に追加", icon: svg(ArrowUpToLine),
              onClick: () => insertRowAbove(editor) }),
    makeBtn({ title: "行を下に追加", icon: svg(ArrowDownToLine),
              onClick: () => insertRowBelow(editor) }),
    makeBtn({ title: "選択行を削除", icon: svg(Minus),
              onClick: () => deleteRow(editor) }),
    sep(),
    // 列
    makeBtn({ title: "列を左に追加", icon: svg(ArrowLeftToLine),
              onClick: () => insertColumnLeft(editor) }),
    makeBtn({ title: "列を右に追加", icon: svg(ArrowRightToLine),
              onClick: () => insertColumnRight(editor) }),
    makeBtn({ title: "選択列を削除", icon: svg(Minus),
              onClick: () => deleteColumn(editor) }),
    sep(),
    // ヘッダー切替 (行 + 列)
    makeBtn({ title: "ヘッダー行を切替", icon: svg(Heading),
              onClick: () => toggleHeaderRow(editor) }),
    makeBtn({ title: "ヘッダー列を切替", icon: svg(Columns3),
              onClick: () => toggleHeaderColumn(editor) }),
    sep(),
    // 結合 / 解除 (条件付き表示)
    mergeBtn,
    unmergeBtn,
    // 整列
    makeBtn({ title: "左寄せ",     icon: svg(AlignLeft),   onClick: () => alignLeft(editor) }),
    makeBtn({ title: "中央寄せ",   icon: svg(AlignCenter), onClick: () => alignCenter(editor) }),
    makeBtn({ title: "右寄せ",     icon: svg(AlignRight),  onClick: () => alignRight(editor) }),
    // 背景色
    colorBtn,
    sep(),
    // テーブル削除
    makeBtn({ title: "テーブル全体を削除", icon: svg(Trash2),
              onClick: () => deleteTable(editor) }),
  )

  // body 直下に append。 surface 内に置くと Lexical reconciler が
  // 「Lexical state に対応しない子要素」 として削除してしまう。
  document.body.appendChild(menu)

  let currentCellDOMs = []  // 単一/複数選択を統一して扱う

  // 複数 cell の bounding box の union を計算
  const unionRect = () => {
    if (currentCellDOMs.length === 0) return null
    let left = Infinity, right = -Infinity, top = Infinity, bottom = -Infinity
    for (const dom of currentCellDOMs) {
      const r = dom.getBoundingClientRect()
      if (r.left   < left)   left   = r.left
      if (r.right  > right)  right  = r.right
      if (r.top    < top)    top    = r.top
      if (r.bottom > bottom) bottom = r.bottom
    }
    return { left, right, top, bottom, width: right - left, height: bottom - top }
  }

  const updatePosition = () => {
    const rect = unionRect()
    if (!rect) return
    const menuH = menu.offsetHeight
    const menuW = menu.offsetWidth
    // 選択範囲の上中央
    let left = rect.left + rect.width / 2 - menuW / 2
    let top  = rect.top - menuH - 6
    if (top < 6) top = rect.bottom + 6
    const maxLeft = window.innerWidth - menuW - 8
    if (left > maxLeft) left = maxLeft
    menu.style.left = `${Math.max(8, left)}px`
    menu.style.top  = `${top}px`
  }

  const update = () => {
    editor.read(() => {
      const sel = $getSelection()
      const cellKeys = new Set()

      if (sel) {
        if ($isTableSelection(sel)) {
          // 複数セル選択 (Cmd+drag 等): 範囲内の全 cell を取る
          for (const n of sel.getNodes()) {
            if ($isTableCellNode(n)) cellKeys.add(n.getKey())
          }
        } else {
          // 単一カーソル: anchor 1 cell
          let node = null
          if ($isRangeSelection(sel)) node = sel.anchor.getNode()
          else if (typeof sel.getNodes === "function") node = sel.getNodes()[0]
          if (node) {
            const cell = $getTableCellNodeFromLexicalNode(node)
            if ($isTableCellNode(cell)) cellKeys.add(cell.getKey())
          }
        }
      }

      currentCellDOMs = Array.from(cellKeys)
        .map((k) => editor.getElementByKey(k))
        .filter(Boolean)

      if (currentCellDOMs.length === 0) {
        menu.style.display = "none"
        summary.style.display = "none"
        return
      }

      // 選択範囲の summary を計算 (行数 × 列数)
      // td/th の rowIndex/cellIndex から rows/cols セットを得る
      const rowSet = new Set()
      const colSet = new Set()
      for (const dom of currentCellDOMs) {
        const tr = dom.parentElement
        if (tr) {
          // 行 index は tr の親 (tbody) 内での position
          const tbody = tr.parentElement
          if (tbody) rowSet.add(Array.from(tbody.children).indexOf(tr))
        }
        // 列 index は tr 内での位置
        if (tr) colSet.add(Array.from(tr.children).indexOf(dom))
      }
      const rows = rowSet.size
      const cols = colSet.size

      if (currentCellDOMs.length > 1) {
        summary.textContent = `${rows} × ${cols} 選択中`
        summary.style.display = "inline-flex"
        // 複数選択 → 「結合」 を表示
        mergeBtn.style.display = "inline-flex"
        unmergeBtn.style.display = "none"
      } else {
        summary.style.display = "none"
        // 単一カーソル: 結合 cell (colSpan>1 or rowSpan>1) なら「解除」を出す
        mergeBtn.style.display = "none"
        editor.read(() => {
          const sel = $getSelection()
          let cell = null
          if ($isRangeSelection(sel)) {
            cell = $getTableCellNodeFromLexicalNode(sel.anchor.getNode())
          }
          const isMerged = cell && $isTableCellNode(cell) &&
                           (cell.getColSpan() > 1 || cell.getRowSpan() > 1)
          unmergeBtn.style.display = isMerged ? "inline-flex" : "none"
        })
      }

      menu.style.display = "flex"
      requestAnimationFrame(updatePosition)
    })
  }

  const unregister = editor.registerUpdateListener(update)
  // scroll や resize でも追従
  const onScrollOrResize = () => { if (currentCellDOMs.length > 0) updatePosition() }
  window.addEventListener("scroll", onScrollOrResize, true)
  window.addEventListener("resize", onScrollOrResize)

  return () => {
    unregister()
    window.removeEventListener("scroll", onScrollOrResize, true)
    window.removeEventListener("resize", onScrollOrResize)
    menu.remove()
  }
}
